import { Component } from '@theme/component';

const TAU = Math.PI * 2;
const COLORS = ['#64b5f6', '#ab68ef', '#ec6da9', '#efa34d', '#72d4bc'];
const GLYPHS = '01アイウエオカキクケコサシスセソタチツテト';
const random = (seed) => {
  const n = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return n - Math.floor(n);
};
const bounce = (n) => 1 - Math.abs(((n % 2) + 2) % 2 - 1);
const MYSTIFY_START = [
  [[.12, .22], [.78, .16], [.9, .78], [.28, .86]],
  [[.18, .78], [.32, .13], [.88, .28], [.7, .9]]
];

/** One bounded, decorative renderer. It never observes or animates store content.
 * @extends {Component<{canvas: HTMLCanvasElement, toggle: HTMLButtonElement}>}
 */
class GTScreensaver extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.controller) return;
    this.controller = new AbortController();
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.connection = navigator.connection;
    this.elapsed = this.dataset.mode === 'mystify' ? 0 : 7;
    this.width = 0;
    this.height = 0;
    delete this.dataset.ready;
    this.raf = 0;
    this.lastTime = 0;
    this.pageHidden = false;
    this.mode = this.dataset.mode;
    this.paused = false;
    try { this.paused = sessionStorage.getItem('gt-screensaver-paused') === '1'; } catch { /* Storage is optional. */ }
    const options = { signal: this.controller.signal };
    this.motion.addEventListener('change', () => this.syncMotion(), options);
    this.connection?.addEventListener('change', () => this.syncMotion(), options);
    document.addEventListener('visibilitychange', () => this.syncMotion(), options);
    window.addEventListener('pagehide', () => { this.pageHidden = true; this.syncMotion(); }, options);
    window.addEventListener('pageshow', () => { this.pageHidden = false; this.syncMotion(); }, options);
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.resize(), 180);
    }, options);
    const initialize = () => {
      if (!this.isConnected || this.controller.signal.aborted) return;
      this.context = this.refs.canvas?.getContext('2d', { alpha: false });
      if (!this.context) return;
      this.sizeObserver = new ResizeObserver(() => this.resize());
      this.sizeObserver.observe(this.refs.canvas);
      this.resize();
    };
    if ('requestIdleCallback' in window) this.idle = window.requestIdleCallback(initialize, { timeout: 1200 });
    else this.startTimer = setTimeout(initialize, 250);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.controller = null;
    cancelAnimationFrame(this.raf);
    if (this.idle) window.cancelIdleCallback(this.idle);
    clearTimeout(this.startTimer);
    clearTimeout(this.resizeTimer);
    this.sizeObserver?.disconnect();
    this.sizeObserver = null;
    this.raf = 0;
    this.context = null;
    this.pipes = null;
  }

  /** Native button supports pointer, Enter, and Space. */
  toggleMotion() {
    this.paused = !this.paused;
    try { sessionStorage.setItem('gt-screensaver-paused', this.paused ? '1' : '0'); } catch { /* Private browsing. */ }
    this.syncMotion();
  }

  syncMotion() {
    if (!this.context || !('ready' in this.dataset)) return;
    const limited = this.motion.matches || this.connection?.saveData;
    const stopped = this.paused || limited;
    this.refs.toggle.hidden = Boolean(limited);
    this.refs.toggle.setAttribute('aria-pressed', String(this.paused));
    this.refs.toggle.setAttribute('aria-label', this.paused ? this.refs.toggle.dataset.playLabel : this.refs.toggle.dataset.pauseLabel);
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.lastTime = 0;
    this.dataset.state = stopped ? 'paused' : (document.hidden || this.pageHidden ? 'hidden' : 'running');
    if (this.dataset.state === 'running') this.raf = requestAnimationFrame((now) => this.tick(now));
  }

  resize() {
    if (!this.context) return;
    const width = Math.round(this.refs.canvas.clientWidth);
    const height = Math.round(this.refs.canvas.clientHeight);
    if (width < 32 || height < 32 || (width === this.width && height === this.height)) return;
    this.width = width;
    this.height = height;
    this.mobile = width < 750;
    this.interval = 1000 / (this.mobile ? 20 : 24);
    const pixelBudget = this.mobile ? 420000 : 1000000;
    const scale = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(pixelBudget / (width * height)));
    this.refs.canvas.width = Math.max(1, Math.round(width * scale));
    this.refs.canvas.height = Math.max(1, Math.round(height * scale));
    this.context.setTransform(scale, 0, 0, scale, 0, 0);
    this.pipes = null;
    this.draw();
    if (!('ready' in this.dataset)) {
      this.dataset.ready = '';
      this.refs.toggle.hidden = false;
      this.syncMotion();
    }
  }

  tick(now) {
    this.raf = 0;
    if (!this.isConnected || document.hidden || this.pageHidden || this.paused || this.motion.matches || this.connection?.saveData) return;
    if (!this.lastTime) this.lastTime = now;
    const delta = now - this.lastTime;
    if (delta >= this.interval) {
      this.elapsed += Math.min(delta - (delta % this.interval), 100) / 1000;
      this.lastTime = now - (delta % this.interval);
      this.draw();
    }
    this.raf = requestAnimationFrame((time) => this.tick(time));
  }

  draw() {
    const ctx = this.context;
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, this.width, this.height);
    // Rebuild bounded trails from time. Every abandoned pixel returns to pure black.
    switch (this.mode) {
      case 'matrix': this.matrix(); break;
      case 'pipes': this.pipeScene(); break;
      case 'mystify': this.mystify(); break;
      case 'ribbons': this.ribbons(); break;
      default: this.drift();
    }
    ctx.globalAlpha = 1;
  }

  drift() {
    const ctx = this.context;
    const w = this.width, h = this.height, t = this.elapsed * .1;
    const bands = this.mobile ? 4 : 6;
    ctx.globalCompositeOperation = 'lighter';
    for (let band = 0; band < bands; band++) {
      const phase = band * 1.12;
      ctx.strokeStyle = COLORS[band % COLORS.length];
      for (let strand = 0; strand < 6; strand++) {
        ctx.beginPath();
        for (let step = 0; step <= 60; step++) {
          const u = step / 60;
          const x = (u * 1.4 - .2) * w;
          const wave = Math.sin(u * 5 + t + phase) * .25 + Math.sin(u * 9 - t * .5 + phase) * .1;
          const y = h * (.5 + wave) + Math.sin(u * 7 + t + strand * .15) * (12 + strand * 5);
          if (step === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.lineWidth = strand === 0 ? 9 : 1.2;
        ctx.globalAlpha = strand === 0 ? .055 : .26 - strand * .025;
        ctx.stroke();
      }
    }
  }

  matrix() {
    const ctx = this.context;
    const size = this.mobile ? 16 : 18;
    const spacing = this.mobile ? 32 : 35;
    const rows = Math.ceil(this.height / size);
    const columns = Math.ceil(this.width / spacing);
    ctx.font = `${size}px ui-monospace, monospace`;
    ctx.textAlign = 'center';
    for (let col = 0; col < columns; col++) {
      const seed = random(col + 11);
      const count = 7 + Math.floor(seed * 10);
      const head = (this.elapsed * (2.1 + seed * 2.3) + seed * (rows + count)) % (rows + count);
      for (let tail = 0; tail < count; tail++) {
        const y = (head - tail) * size;
        if (y < -size || y > this.height + size) continue;
        ctx.fillStyle = tail === 0 ? '#b8ffdc' : '#30bf68';
        ctx.globalAlpha = (1 - tail / count) * (tail === 0 ? .8 : .5);
        const index = Math.floor(random(col * 91 + tail * 13 + Math.floor(this.elapsed * 2)) * GLYPHS.length);
        ctx.fillText(GLYPHS[index], col * spacing + spacing / 2, y);
      }
    }
  }

  mystify() {
    const ctx = this.context;
    const t = this.elapsed;
    const trails = this.mobile ? 12 : 18;
    ctx.lineWidth = 1.35;
    ctx.lineJoin = 'round';
    for (let shape = 0; shape < 2; shape++) {
      for (let trail = Math.min(trails - 1, Math.floor(t / .13)); trail >= 0; trail--) {
        const time = t - trail * .13;
        ctx.strokeStyle = `hsl(${(time * 7 + shape * 155 + 269) % 360} 78% 63%)`;
        ctx.globalAlpha = .66 * Math.pow(1 - trail / trails, 1.7);
        ctx.beginPath();
        for (let vertex = 0; vertex < 4; vertex++) {
          const seed = shape * 5 + vertex + 1;
          const [startX, startY] = MYSTIFY_START[shape][vertex];
          const dx = (vertex % 2 ? -1 : 1) * (.016 + random(seed + 10) * .015);
          const dy = (vertex < 2 ? 1 : -1) * (.019 + random(seed + 40) * .017);
          const x = (.05 + .9 * bounce((startX - .05) / .9 + time * dx)) * this.width;
          const y = (.05 + .9 * bounce((startY - .05) / .9 + time * dy)) * this.height;
          if (vertex === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }
  }

  ribbons() {
    const ctx = this.context;
    const w = this.width, h = this.height, t = this.elapsed * .15;
    ctx.globalCompositeOperation = 'lighter';
    const count = this.mobile ? 3 : 5;
    for (let ribbon = 0; ribbon < count; ribbon++) {
      const phase = ribbon * .65;
      const points = [];
      for (let i = 0; i <= 60; i++) {
        const u = i / 60;
        points.push({
          x: (u * 1.35 - .175) * w,
          y: h * (.5 + .3 * Math.sin(u * 6 + t + phase) + .13 * Math.cos(u * 3 - t * .7 + phase)),
          thickness: Math.sin(u * 7 - t * .6 + phase) * (this.mobile ? 14 : 28)
        });
      }
      const gradient = ctx.createLinearGradient(0, 0, w, h);
      gradient.addColorStop(0, '#aa194a');
      gradient.addColorStop(.45, '#f06028');
      gradient.addColorStop(.8, '#f5b54c');
      gradient.addColorStop(1, '#e74725');
      ctx.fillStyle = gradient;
      ctx.globalAlpha = .19;
      ctx.beginPath();
      points.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y - p.thickness); else ctx.lineTo(p.x, p.y - p.thickness); });
      for (let i = points.length - 1; i >= 0; i--) ctx.lineTo(points[i].x, points[i].y + points[i].thickness);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = .65;
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  buildPipes(cycle) {
    const paths = [];
    const count = this.mobile ? 3 : 4;
    for (let pipe = 0; pipe < count; pipe++) {
      const points = [];
      let point = [random(pipe + cycle * 10) * 2 - 1, random(pipe + 81) * 2 - 1, random(pipe + 55) * 1.4 - .7];
      points.push(point);
      let previousAxis = -1;
      for (let segment = 0; segment < 19; segment++) {
        const seed = cycle * 113 + pipe * 71 + segment * 7;
        let axis = Math.floor(random(seed + 4) * 3);
        if (axis === previousAxis) axis = (axis + 1) % 3;
        previousAxis = axis;
        const next = [...point];
        const direction = point[axis] > .6 ? -1 : point[axis] < -.6 ? 1 : random(seed + 5) > .5 ? 1 : -1;
        next[axis] = Math.max(-1, Math.min(1, point[axis] + direction * (.3 + random(seed + 6) * .5)));
        points.push(next);
        point = next;
      }
      paths.push({ points, hue: (pipe * 88 + cycle * 43 + 205) % 360 });
    }
    this.pipes = { cycle, paths };
  }

  pipeScene() {
    const ctx = this.context;
    const cycle = Math.floor(this.elapsed / 34);
    const age = this.elapsed % 34;
    if (this.pipes?.cycle !== cycle) this.buildPipes(cycle);
    const fade = Math.min(1, age / 1.5, (34 - age) / 2);
    const progress = Math.min(19, 3 + age * .65);
    const angle = this.elapsed * .012;
    const project = (point) => {
      const x = point[0] * Math.cos(angle) + point[2] * Math.sin(angle);
      const z = -point[0] * Math.sin(angle) + point[2] * Math.cos(angle);
      const scale = 2.8 / (3.5 + z);
      return { x: this.width * (.5 + (x + z * .28) * .46 * scale), y: this.height * (.5 + (point[1] - z * .18) * .46 * scale), z, scale };
    };
    const segments = [];
    for (const pipe of this.pipes.paths) {
      for (let i = 0; i < Math.ceil(progress); i++) {
        const start = pipe.points[i];
        const end = pipe.points[i + 1];
        const fraction = Math.min(1, progress - i);
        const a = project(start);
        const b = project(start.map((v, axis) => v + (end[axis] - v) * fraction));
        segments.push({ a, b, hue: pipe.hue, depth: (a.z + b.z) / 2 });
      }
    }
    segments.sort((a, b) => b.depth - a.depth);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const { a, b, hue } of segments) {
      const diameter = (this.mobile ? 9 : 13) * (a.scale + b.scale) / 2;
      ctx.globalAlpha = fade * .85;
      for (let layer = 0; layer < 3; layer++) {
        ctx.lineWidth = diameter * (1 - layer * .3);
        ctx.strokeStyle = `hsl(${hue} 58% ${22 + layer * 15}%)`;
        ctx.beginPath();
        ctx.moveTo(a.x - layer * .65, a.y - layer * .65);
        ctx.lineTo(b.x - layer * .65, b.y - layer * .65);
        ctx.stroke();
      }
    }
  }
}

if (!customElements.get('gt-screensaver')) customElements.define('gt-screensaver', GTScreensaver);
